import { Injectable } from '@angular/core'
import { Observable, of } from 'rxjs'
import { HttpClient } from '@angular/common/http'
import { concatMap, map, switchMap, toArray } from 'rxjs/operators'
import * as R from 'ramda'
import { Invoice, InvoiceItem, InvoiceLineItemWithTotal, InvoiceTotalResponse } from './invoice.model'
import { deserializeArray, HttpGetRequestCacheService, isSomething } from '@tokeet-frontend/tv3-platform'
import { InvoiceService as SingleInvoiceService } from '@tv3/store/invoice/invoice.service'
import { InquiryService } from '@tv3/store/inquiry/inquiry.service'
import { PreferenceService } from '@tv3/store/preferences/preference.service'
import * as lodash from 'lodash'

@Injectable({
  providedIn: 'root',
})
export class InvoiceService {
  constructor(
    private http: HttpClient,
    private invoice: SingleInvoiceService,
    private inquiries: InquiryService,
    private preferenceService: PreferenceService,
    private httpCache: HttpGetRequestCacheService
  ) {}

  prepare(data: { line_items: InvoiceItem[]; days: number; guests: number }): Observable<InvoiceTotalResponse> {
    const url = `@api/invoice/items/prepare`
    return this.http
      .put<{
        invoice: { subtotal: number; total: number; total_tax: number }
        line_items: InvoiceLineItemWithTotal[]
      }>(url, data)
      .pipe(
        map((data) => ({
          ...data,
          ...data.invoice,
          line_items: lodash.sortBy(data.line_items, (t) => t.line_number),
        }))
      )
  }

  total(invoiceId: string): Observable<InvoiceTotalResponse> {
    const url = `@api/invoice/total/${invoiceId}`
    return this.httpCache
      .cacheGetRequest<{
        total: number
        pkey: string
        inquiry_id: string
        invoice: { total: number; subtotal: number; total_tax: number; line_items: InvoiceLineItemWithTotal[] }
      }>(url, { ttl: 5 * 60 * 1000 })
      .pipe(
        map((data) => ({
          ...data,
          ...data.invoice,
          line_items: lodash.sortBy(data.invoice.line_items, (t) => t.line_number),
        }))
      )
  }

  totalBatch(invoiceIds: string[], refresh?: boolean) {
    if (!isSomething(invoiceIds)) {
      return of({} as Record<string, number>)
    }

    const limit = 500
    const chunks = lodash.chunk(lodash.sortBy(invoiceIds), limit)

    const url = `@api/invoice/total/all`
    return of(...chunks).pipe(
      concatMap((chunk) =>
        this.httpCache.cachePutRequest<{ pkey: string; total: number }[]>(
          url,
          { invoices: chunk, limit },
          { ttl: 5 * 60 * 1000, refresh }
        )
      ),
      toArray(),
      map((data) => lodash.flatten(data)),
      map((response: { pkey: string; total: number }[]) =>
        lodash.mapValues(lodash.keyBy(response, 'pkey'), (t) => +t.total || 0)
      )
    )
  }

  add(payload): Observable<Invoice> {
    const url = `@api/invoice`
    return this.http.post(url, payload).pipe(
      map((response) => {
        return Invoice.deserialize({ ...response, notes: payload.notes }) // TV3-2752 - backend doesn't return notes
      })
    )
  }

  get(invoiceId: string): Observable<Invoice> {
    const url = `@api/invoice/${invoiceId}`

    return this.http.get(url).pipe(map((response) => Invoice.deserialize(response)))
  }

  fetch(ids: string[]): Observable<Invoice[]> {
    const url = `@api/invoice/all/`

    if (!isSomething(ids) || !R.is(Array, ids)) {
      return of([])
    }

    return this.http.post(url, { pkeys: ids }).pipe(deserializeArray<Invoice>(Invoice))
  }

  update(invoiceId: string, data): Observable<Invoice> {
    const url = `@api/invoice/update/${invoiceId}`
    return this.http.put(url, data).pipe(map((response) => Invoice.deserialize(response)))
  }

  status(invoiceId: string, status: 'paid' | 'unpaid' | 'cancel') {
    const url = `@api/invoice/${status}/${invoiceId}`
    return this.http.put(url, {}).pipe(map((response) => Invoice.deserialize(response)))
  }

  schedule(invoiceId: string, date: number) {
    const url = `@api/invoice/schedule/${invoiceId}`
    return this.http.put(url, { time: date })
  }

  refund(invoiceId: string) {
    const url = `@api/invoice/refunded/${invoiceId}`

    return this.http.put(url, {}).pipe(map((response) => Invoice.deserialize(response)))
  }

  archive(id: string): Observable<Invoice> {
    const url = `@api/invoice/archive/${id}`

    return this.http.put(url, {}).pipe(map((response) => Invoice.deserialize(response)))
  }
  archiveBatch(ids: string[]) {
    return of(...ids).pipe(
      concatMap((id) => this.archive(id)),
      toArray()
    )
  }

  unarchive(id: string): Observable<Invoice> {
    const url = `@api/invoice/unarchive/${id}`

    return this.http.put(url, {}).pipe(map((response) => Invoice.deserialize(response)))
  }
  unarchiveBatch(ids: string[]) {
    return of(...ids).pipe(
      concatMap((id) => this.unarchive(id)),
      toArray()
    )
  }

  delete(id: string) {
    const url = `@api/invoice/delete/${id}`

    return this.http.delete(url, {})
  }

  deleteBatch(ids: string[]) {
    return of(...ids).pipe(
      concatMap((id) => this.delete(id)),
      toArray()
    )
  }

  disablePayment(invoiceId) {
    const url = `@api/invoice/onlinepayment/disable/${invoiceId}`
    return this.http.put(url, { online_payment: 0 })
  }

  enablePayment(invoiceId) {
    const url = `@api/invoice/onlinepayment/enable/${invoiceId}`
    return this.http.put(url, { online_payment: 1 })
  }

  setGateway(invoiceId, gatewayId) {
    const url = `@api/invoice/gateway/${invoiceId}`
    return this.http.put(url, { gateway_id: gatewayId })
  }

  payWithSavedCard(invoiceId: string, total: number, description: string): Observable<Invoice> {
    const url = `@api/invoice/charge/${invoiceId}`
    return this.http.put(url, { total, description }).pipe(map((response) => Invoice.deserialize(response)))
  }

  newInvoicePayload(invoice: Invoice) {
    const invoiceEditableFields = [
      'currency',
      'due_date',
      'expires_at',
      'guest_id',
      'guest_name',
      'inquiry_id',
      'invoice_date',
      'invoice_items',
      'invoice_num',
      'notes',
      'invoice_header',
      'payment_instructions',
      'payment_terms',
      'rental_id',
      'rental_name',
      'type',
      'address_key',
    ]

    const invoicePayload: any = R.pick(invoiceEditableFields, invoice.serialize())

    return {
      ...invoicePayload,
      invoice_date: invoicePayload.invoice_date,
      due_date: invoicePayload.due_date,
      expires_at: invoicePayload.expires_at,
    }
  }

  editInvoicePayload(invoice: Invoice) {
    const invoiceEditableFields = [
      'currency',
      'due_date',
      'expires_at',
      'guest_id',
      'guest_name',
      'invoice_date',
      'invoice_items',
      'notes',
      'invoice_header',
      'payment_instructions',
      'payment_terms',
      'rental_name',
    ]

    return R.pick(invoiceEditableFields, invoice.serialize())
  }

  getGuestInvoice(invoice: Invoice) {
    const url = `@api/invoice/preview/guest/${invoice.created}/${invoice.invoiceNum}/${invoice.id}/${invoice.publicKey}`

    return this.http.get(url)
  }

  sendBatch(invoices: Invoice[]) {
    return this.preferenceService.get().pipe(
      switchMap((preferences) =>
        of(...invoices).pipe(
          concatMap((invoice) =>
            this.inquiries.load(invoice.inquiryId).pipe(map((inquiry) => ({ inquiry, invoice, preferences })))
          ),
          concatMap(({ inquiry, invoice, preferences }) => this.invoice.send(invoice, inquiry, preferences)),
          toArray()
        )
      )
    )
  }
}
